/* "Someone else is editing this."
 *
 * A shared project is opened by whoever asks for it, but only one person at a
 * time can save. Everyone else gets the project read-only, and this is the one
 * thing on screen that says so.
 *
 * It is a banner rather than a disabled interface on purpose. Greying out every
 * control in a 3D editor would be a large change touching every panel, and it
 * would still leave someone wondering why. A sentence naming the colleague who
 * has it answers the question the greying-out would only raise -- and the work
 * itself is never at risk, because the refusal that matters lives in the
 * database, not here.
 *
 * services/projects.js emits 'project:turn' whenever the answer changes: on
 * opening a project, on starting a new one, and when a save discovers the turn
 * has been taken over.
 */

import { on } from '../state/bus.js';
import { el } from '../state/store.js';
import { takeOverProject } from '../services/projects.js';
import { toast } from './toast.js';

let bar = null;

function ensureBar() {
  if (bar) return bar;
  bar = el('div', 'turnbar hidden');
  document.body.appendChild(bar);
  addEventListener('resize', () => { if (!bar.classList.contains('hidden')) sit(); });
  return bar;
}

/* Below the top bar, wherever that currently ends. It is a grid row that wraps
   to a second line under about 1100px, so its height is not something the
   stylesheet can hardcode — and getting it wrong put this over the account
   button, which is how you leave a project you cannot edit. */
function sit() {
  const top = document.querySelector('.topbar')?.getBoundingClientRect().bottom ?? 48;
  bar.style.top = Math.round(top + 8) + 'px';
}

function paint({ readOnly, heldBy, available }) {
  const b = ensureBar();
  if (!readOnly && !available) { b.classList.add('hidden'); return; }

  b.innerHTML = '';

  if (available) {
    /* Free again. The offer is a button rather than something that happens by
       itself: whoever had it has been saving, so the scene on this screen is
       behind theirs, and taking over means reloading. Doing that underneath
       somebody without asking would replace what they are looking at. */
    b.classList.add('turnbar--free');
    b.appendChild(el('strong', null,
      heldBy ? `${heldBy} has finished with this project.` : 'This project is free again.'));
    const go = el('button', 'btn btn--sm btn--pri turnbar__take', 'Open for editing');
    go.onclick = async () => {
      go.disabled = true;
      go.textContent = 'Opening…';
      try { await takeOverProject(); }
      catch (err) { toast(err.message || 'Couldn’t open it for editing.', true); }
      finally { go.disabled = false; go.textContent = 'Open for editing'; }
    };
    b.appendChild(go);
    b.classList.remove('hidden');
    sit();
    return;
  }

  b.classList.remove('turnbar--free');
  b.appendChild(el('strong', null, heldBy ? `${heldBy} is editing this project.` : 'Someone else is editing this project.'));
  /* Said plainly, because the consequence is the part that matters and it is
     not obvious from "read-only": you can look, you can take things out of it,
     but anything you change here is going nowhere.

     "You'll be told" is a promise this file now keeps — services/projects.js
     watches the project while you wait. It used to say the project "becomes
     editable again when they close it", which was simply untrue: nothing
     re-checked, and the only way out was to reload. */
  b.appendChild(el('span', null,
    ' You can look around, but changes you make will not be saved. You’ll be told the moment they close it.'));
  b.classList.remove('hidden');
  sit();
}

on('project:turn', paint);
